/* eslint-disable no-underscore-dangle */

'use client';

import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { PaymentMethodEnum } from '@/__generated_graphql__/types';
import { PaymentTypeEnum, ROUTES } from '@/constants/common';
import { CardAd } from '@/types';

import { mapTypenameToAdType } from '../AdvertisePayments.helpers';
import { UsePaymentModalsManagerResult } from './types';

type UsePaymentSuccessProps = {
  transformedAd: CardAd;
  isAddBalance: boolean;
  type: PaymentTypeEnum;
  mainModal: { close: () => void };
  modals: UsePaymentModalsManagerResult;
  successCallback: (ad: CardAd, isAddBalance?: boolean) => Promise<void>;
};

type ReturnType = {
  handleSuccess: (method?: PaymentMethodEnum) => void;
  isSuccessTopUp: boolean;
};

export const usePaymentSuccess = ({
  transformedAd,
  isAddBalance,
  type,
  mainModal,
  modals,
  successCallback
}: UsePaymentSuccessProps): ReturnType => {
  const router = useRouter();

  const client = useApolloClient();

  useEffect(() => {
    successCallback?.(transformedAd, isAddBalance);
  }, [transformedAd?.id, isAddBalance]);

  const [isSuccessTopUp, setIsSuccessTopUp] = useState<boolean>(false);

  const successWithRedirect = () => {
    const adType = mapTypenameToAdType(transformedAd.__typename);
    router.push(
      `${ROUTES.PROFILE}?showModal=true&adId=${transformedAd.id}&adType=${adType}`
    );
  };

  const successTopUpBalance = () => {
    modals.successTopUp.open();
    setIsSuccessTopUp(true);
  };

  const handleSuccess: ReturnType['handleSuccess'] = useCallback(
    method => {
      const isPublishType = type === PaymentTypeEnum.PUBLICATION;
      const isTopUpType = type === PaymentTypeEnum.TOP_UP_BALANCE;
      const isWalletMethod = method === PaymentMethodEnum.Wallet;

      const shouldRedirect: boolean =
        isPublishType && !isAddBalance && !!transformedAd.__typename;

      const shouldCloseModal: boolean =
        !isAddBalance || isPublishType || isTopUpType;

      if (isWalletMethod || isTopUpType) {
        client.refetchQueries({
          include: ['ProfileFull']
        });
      }

      if (shouldCloseModal) {
        mainModal.close();
      }

      successCallback(transformedAd);

      if (shouldRedirect) {
        successWithRedirect();
        return;
      }

      if (isAddBalance) {
        successTopUpBalance();
        return;
      }

      modals.success.open();
    },
    [
      transformedAd,
      isAddBalance,
      type,
      mainModal,
      modals,
      successCallback,
      client
    ]
  );

  return { handleSuccess, isSuccessTopUp };
};
